import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Package, 
  Plus, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  CheckCircle,
  XCircle,
  AlertCircle,
  Calculator,
  Eye,
  Edit,
  Trash2,
  RefreshCw
} from "lucide-react";

const categories = [
  { value: "iphone", label: "iPhone" },
  { value: "macbook", label: "MacBook" },
  { value: "ipad", label: "iPad" },
  { value: "airpods", label: "AirPods" },
  { value: "watch", label: "Apple Watch" },
];

const conditions = [
  { value: "new", label: "Nuevo" },
  { value: "cpo", label: "CPO (Certificado)" },
  { value: "used", label: "Usado" },
];

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  published: "bg-blue-100 text-blue-800",
};

const statusLabels = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  published: "Publicado",
};

export default function PartnerDashboard({ user, logout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [products, setProducts] = useState([]);
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newProduct, setNewProduct] = useState({
    product_name: "",
    product_description: "",
    product_image: "",
    product_category: "",
    product_condition: "",
    partner_cost: "",
    suggested_retail_price: "",
    stock_quantity: ""
  });

  const [calcInputs, setCalcInputs] = useState({
    partner_cost: 500,
    sale_price: 600,
    quantity: 10
  });
  const [calcResult, setCalcResult] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "partner") {
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, productsRes] = await Promise.all([
        axios.get(`${API}/partner/dashboard`),
        axios.get(`${API}/partner/products`)
      ]);
      setDashboardData(dashboardRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("No tienes acceso al panel de partners");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/products`, {
        ...newProduct,
        partner_cost: parseFloat(newProduct.partner_cost),
        suggested_retail_price: parseFloat(newProduct.suggested_retail_price),
        stock_quantity: parseInt(newProduct.stock_quantity)
      });
      toast.success("Producto enviado para revisión");
      setShowNewProductDialog(false);
      setNewProduct({
        product_name: "",
        product_description: "",
        product_image: "",
        product_category: "",
        product_condition: "",
        partner_cost: "",
        suggested_retail_price: "",
        stock_quantity: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar el producto");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm("¿Seguro que quieres eliminar este producto?")) return;
    
    try {
      await axios.delete(`${API}/partner/products/${productId}`);
      toast.success("Producto eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const calculateProfit = async () => {
    try {
      const res = await axios.get(`${API}/partner/calculator`, {
        params: calcInputs
      });
      setCalcResult(res.data);
    } catch (error) {
      toast.error("Error al calcular");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} logout={logout} />
        <div className="flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} logout={logout} />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="partner-dashboard-title">
                Panel de Partner
              </h1>
              <p className="text-muted-foreground mt-1">
                {dashboardData?.partner?.company_name}
              </p>
            </div>
            <div className="flex gap-3 mt-4 md:mt-0">
              <Button variant="outline" onClick={() => setShowCalculator(true)} data-testid="open-calculator-btn">
                <Calculator className="h-4 w-4 mr-2" />
                Calculadora
              </Button>
              <Button onClick={() => setShowNewProductDialog(true)} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Producto
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card data-testid="stat-products">
              <CardHeader className="pb-2">
                <CardDescription>Productos totales</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.total_products || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.pending_products || 0} pendientes · {stats.published_products || 0} publicados
                </p>
              </CardContent>
            </Card>

            <Card data-testid="stat-sales">
              <CardHeader className="pb-2">
                <CardDescription>Ventas totales</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.total_sales || 0}</div>
                <p className="text-xs text-muted-foreground">unidades vendidas</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-earnings">
              <CardHeader className="pb-2">
                <CardDescription>Ganancias</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {stats.total_earnings?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) || '0€'}
                </div>
                <p className="text-xs text-muted-foreground">confirmadas</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-pending">
              <CardHeader className="pb-2">
                <CardDescription>Pendiente de cobro</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">
                  {stats.pending_earnings?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) || '0€'}
                </div>
                <p className="text-xs text-muted-foreground">en ofertas activas</p>
              </CardContent>
            </Card>
          </div>

          {/* Commission info */}
          <Card className="mb-8 bg-primary/5 border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <span className="font-medium">Tu comisión BulkBytes:</span>
                  <Badge variant="secondary" className="text-lg">
                    {stats.commission_rate || 10}%
                  </Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowCalculator(true)}>
                  Calcular ganancias
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Products */}
          <Tabs defaultValue="all" className="space-y-6">
            <TabsList data-testid="product-tabs">
              <TabsTrigger value="all">Todos ({products.length})</TabsTrigger>
              <TabsTrigger value="pending">Pendientes ({products.filter(p => p.status === 'pending').length})</TabsTrigger>
              <TabsTrigger value="approved">Aprobados ({products.filter(p => p.status === 'approved').length})</TabsTrigger>
              <TabsTrigger value="published">Publicados ({products.filter(p => p.status === 'published').length})</TabsTrigger>
            </TabsList>

            {["all", "pending", "approved", "published"].map(tab => (
              <TabsContent key={tab} value={tab}>
                <Card>
                  <CardHeader>
                    <CardTitle>Mis Productos</CardTitle>
                    <CardDescription>
                      Gestiona los productos que has enviado a BulkBytes
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {products.filter(p => tab === "all" || p.status === tab).length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No hay productos en esta categoría</p>
                        <Button className="mt-4" onClick={() => setShowNewProductDialog(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Añadir producto
                        </Button>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Producto</TableHead>
                            <TableHead>Categoría</TableHead>
                            <TableHead>Tu coste</TableHead>
                            <TableHead>Precio venta</TableHead>
                            <TableHead>Stock</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead>Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {products
                            .filter(p => tab === "all" || p.status === tab)
                            .map(product => (
                            <TableRow key={product.product_id} data-testid={`product-row-${product.product_id}`}>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <img 
                                    src={product.product_image} 
                                    alt={product.product_name}
                                    className="w-12 h-12 object-cover rounded-lg"
                                  />
                                  <div>
                                    <p className="font-medium">{product.product_name}</p>
                                    <p className="text-xs text-muted-foreground">{product.product_condition}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>{product.product_category}</TableCell>
                              <TableCell>{product.partner_cost}€</TableCell>
                              <TableCell>
                                {product.final_retail_price ? (
                                  <span className="font-medium">{product.final_retail_price}€</span>
                                ) : (
                                  <span className="text-muted-foreground">{product.suggested_retail_price}€ (sugerido)</span>
                                )}
                              </TableCell>
                              <TableCell>{product.stock_quantity}</TableCell>
                              <TableCell>
                                <Badge className={statusColors[product.status]}>
                                  {statusLabels[product.status]}
                                </Badge>
                                {product.rejection_reason && (
                                  <p className="text-xs text-red-600 mt-1">{product.rejection_reason}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  {product.status === "pending" && (
                                    <Button 
                                      variant="ghost" 
                                      size="icon"
                                      onClick={() => handleDeleteProduct(product.product_id)}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </main>

      {/* New Product Dialog */}
      <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Producto</DialogTitle>
            <DialogDescription>
              Añade un producto para que BulkBytes lo revise y publique
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitProduct} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product_name">Nombre del producto *</Label>
                <Input
                  id="product_name"
                  required
                  value={newProduct.product_name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, product_name: e.target.value }))}
                  placeholder="iPhone 15 Pro Max 256GB"
                  data-testid="new-product-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_image">URL de imagen *</Label>
                <Input
                  id="product_image"
                  required
                  value={newProduct.product_image}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, product_image: e.target.value }))}
                  placeholder="https://..."
                  data-testid="new-product-image"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="product_description">Descripción *</Label>
              <Textarea
                id="product_description"
                required
                value={newProduct.product_description}
                onChange={(e) => setNewProduct(prev => ({ ...prev, product_description: e.target.value }))}
                placeholder="Describe el producto, estado, accesorios incluidos..."
                rows={3}
                data-testid="new-product-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoría *</Label>
                <Select
                  value={newProduct.product_category}
                  onValueChange={(value) => setNewProduct(prev => ({ ...prev, product_category: value }))}
                  required
                >
                  <SelectTrigger data-testid="new-product-category">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Condición *</Label>
                <Select
                  value={newProduct.product_condition}
                  onValueChange={(value) => setNewProduct(prev => ({ ...prev, product_condition: value }))}
                  required
                >
                  <SelectTrigger data-testid="new-product-condition">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map(cond => (
                      <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="partner_cost">Tu coste (€) *</Label>
                <Input
                  id="partner_cost"
                  type="number"
                  step="0.01"
                  required
                  value={newProduct.partner_cost}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, partner_cost: e.target.value }))}
                  placeholder="500"
                  data-testid="new-product-cost"
                />
                <p className="text-xs text-muted-foreground">Lo que te cuesta el producto</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="suggested_retail_price">Precio sugerido (€) *</Label>
                <Input
                  id="suggested_retail_price"
                  type="number"
                  step="0.01"
                  required
                  value={newProduct.suggested_retail_price}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, suggested_retail_price: e.target.value }))}
                  placeholder="600"
                  data-testid="new-product-price"
                />
                <p className="text-xs text-muted-foreground">BulkBytes fija el precio final</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stock_quantity">Stock disponible *</Label>
                <Input
                  id="stock_quantity"
                  type="number"
                  required
                  value={newProduct.stock_quantity}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, stock_quantity: e.target.value }))}
                  placeholder="10"
                  data-testid="new-product-stock"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewProductDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-new-product">
                {submitting ? "Enviando..." : "Enviar para revisión"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Calculator Dialog */}
      <Dialog open={showCalculator} onOpenChange={setShowCalculator}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Calculadora de Rendimientos</DialogTitle>
            <DialogDescription>
              Calcula tus ganancias estimadas antes de subir un producto
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tu coste (€)</Label>
                <Input
                  type="number"
                  value={calcInputs.partner_cost}
                  onChange={(e) => setCalcInputs(prev => ({ ...prev, partner_cost: parseFloat(e.target.value) || 0 }))}
                  data-testid="calc-cost"
                />
              </div>
              <div className="space-y-2">
                <Label>Precio venta (€)</Label>
                <Input
                  type="number"
                  value={calcInputs.sale_price}
                  onChange={(e) => setCalcInputs(prev => ({ ...prev, sale_price: parseFloat(e.target.value) || 0 }))}
                  data-testid="calc-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Cantidad</Label>
                <Input
                  type="number"
                  value={calcInputs.quantity}
                  onChange={(e) => setCalcInputs(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  data-testid="calc-quantity"
                />
              </div>
            </div>

            <Button onClick={calculateProfit} className="w-full" data-testid="calc-btn">
              <Calculator className="h-4 w-4 mr-2" />
              Calcular
            </Button>

            {calcResult && (
              <div className="space-y-4 pt-4 border-t" data-testid="calc-result">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-muted-foreground">Ingresos brutos</p>
                    <p className="text-lg font-semibold">{calcResult.breakdown.gross_revenue}€</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-muted-foreground">Comisión BulkBytes ({calcResult.breakdown.commission_rate}%)</p>
                    <p className="text-lg font-semibold text-red-600">-{calcResult.breakdown.bulkbytes_commission}€</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-muted-foreground">Tu coste total</p>
                    <p className="text-lg font-semibold text-red-600">-{calcResult.breakdown.total_product_cost}€</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-muted-foreground">Envío a BulkBytes (est.)</p>
                    <p className="text-lg font-semibold text-red-600">-{calcResult.breakdown.shipping_to_bulkbytes}€</p>
                  </div>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-green-800">Beneficio neto estimado</p>
                      <p className="text-2xl font-bold text-green-600">{calcResult.result.net_profit}€</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-800">Por unidad</p>
                      <p className="text-lg font-semibold text-green-600">{calcResult.result.profit_per_unit}€</p>
                    </div>
                  </div>
                  <p className="text-sm text-green-700 mt-2">
                    Margen: {calcResult.result.profit_margin_percent}%
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
